import { format, formatDistanceToNow, parseISO, isValid } from 'date-fns';
import type { Locale } from 'date-fns';
import { resolveDateFnsLocale } from './date-fns-locale';

/**
 * `roundingMode` is part of Intl.NumberFormat v3 (ES2023). The project targets
 * ES2020, so it is applied through a widened cast. Runtimes without support
 * ignore the option and fall back to the previous behaviour; supported runtimes
 * (Chrome 106+, Safari 15.4+, Firefox 116+) match the backend's ROUND_HALF_UP.
 */
type NumberFormatOptions = Intl.NumberFormatOptions & { roundingMode?: string };

const HALF_UP = { roundingMode: 'halfExpand' } as NumberFormatOptions;

/**
 * Parse a value that may be a date-only string, a full ISO instant, or a Date.
 *
 * `new Date('2026-08-01')` parses an ISO date-only string as UTC midnight, so
 * rendering it in a timezone behind UTC yields the *previous* day. `parseISO`
 * treats date-only strings as local midnight, which is what a calendar date
 * means. Callers rendering financial or scheduling dates must use this rather
 * than the `Date` constructor.
 */
export function parseDateValue(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  try {
    const parsed = parseISO(value);
    return isValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Render a date in the reader's own timezone using `Intl`.
 *
 * Use for display where the exact locale formatting is not important. For
 * surfaces that must match the property locale, prefer `formatDate`.
 */
export function formatDateInViewerTimezone(
  value: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' }
): string {
  const parsed = parseDateValue(value);
  if (!parsed) return '-';
  try {
    return new Intl.DateTimeFormat(undefined, options).format(parsed);
  } catch {
    return parsed.toISOString().slice(0, 10);
  }
}

/**
 * Parse a `YYYY-MM` period string (as used by billing periods) into a local
 * Date anchored on the first of the month.
 *
 * `new Date('2026-08-01')` is UTC midnight, so `getMonth()` reports July for
 * behind-UTC timezones and a period silently renders as the wrong month.
 */
export function parsePeriod(period: string | null | undefined): Date | null {
  if (!period) return null;
  const match = /^(\d{4})-(\d{2})/.exec(period.trim());
  if (!match) return null;
  const [, year, month] = match;
  const d = new Date(Number(year), Number(month) - 1, 1);
  return isValid(d) ? d : null;
}

/**
 * Format a `YYYY-MM` billing period, e.g. `2026-08` -> "August 2026".
 *
 * Month-only formatting is timezone-safe by construction: the day component is
 * never rendered, so a UTC-vs-local midnight difference cannot change the
 * displayed month.
 */
export function formatPeriod(
  period: string | null | undefined,
  pattern: string = 'MMMM yyyy',
  locale?: Locale
): string {
  const d = parsePeriod(period);
  if (!d) return '-';
  return format(d, pattern, locale ? { locale } : undefined);
}

/**
 * Format a monetary amount for display.
 *
 * `currency` and `locale` are required on purpose: a default of EUR/el-GR lets a
 * caller silently render euros in a GBP property, which is invisible until a
 * tenant is billed the wrong figure. Callers get both from `useLocale()`.
 *
 * Rounding is pinned to half-up to match the backend's
 * `quantize_money` (Q7), so a displayed total always equals the stored value.
 */
export function formatMoney(
  amount: number | string | null | undefined,
  currency: string,
  locale: string,
  opts: { showSign?: boolean } = {}
): string {
  const value = typeof amount === 'string' ? parseFloat(amount) : Number(amount ?? 0);
  if (!Number.isFinite(value)) {
    return formatMoney(0, currency, locale, opts);
  }

  const sign = opts.showSign && value > 0 ? '+' : '';

  try {
    const formatted = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      // Match the backend's Decimal quantize(ROUND_HALF_UP) so a displayed
      // amount never disagrees with the stored one.
      ...HALF_UP,
    } as NumberFormatOptions).format(value);
    return sign ? `${sign}${formatted}` : formatted;
  } catch {
    // Unknown currency or locale code: fall back to a plain decimal rather
    // than throwing mid-render.
    return `${sign}${value.toFixed(2)} ${currency}`;
  }
}

/**
 * Format a millesimal share or other ratio: fixed precision, no currency
 * symbol, independent of the property currency (Q8).
 */
export function formatDecimal(
  value: number | string | null | undefined,
  decimals: number = 3,
  locale: string = 'en-GB'
): string {
  const num = typeof value === 'string' ? parseFloat(value) : Number(value ?? 0);
  if (isNaN(num)) return (0).toFixed(decimals);

  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      ...HALF_UP,
    } as NumberFormatOptions).format(num);
  } catch {
    return num.toFixed(decimals);
  }
}

// Context-dependent date styles (Q5). Documents and the resident portal use
// `medium`/`long` for localised month names; dense tables use `short` for a
// compact numeric form.
export const DATE_STYLES = {
  numeric: 'dd/MM/yyyy',
  short: 'dd/MM/yy',
  medium: 'd MMM yyyy',
  long: 'd MMMM yyyy',
  datetime: 'dd/MM/yy HH:mm',
  datetimeLong: 'd MMM yyyy, HH:mm',
} as const;

export type DateStyle = keyof typeof DATE_STYLES;

/**
 * Format a date using an explicit style and locale.
 *
 * Date-only strings are parsed as local midnight (see `parseDateValue`), so a
 * calendar date never shifts a day. Pass `style: 'short'` in dense tables and
 * `'medium'`/`'long'` on documents.
 */
export function formatDate(
  dateValue: string | Date | null | undefined,
  locale: string = 'en-GB',
  style: DateStyle = 'short'
): string {
  const d = parseDateValue(dateValue);
  if (!d) return typeof dateValue === 'string' && dateValue ? dateValue : '-';
  try {
    return format(d, DATE_STYLES[style], { locale: resolveDateFnsLocale(locale) });
  } catch {
    return format(d, DATE_STYLES.numeric);
  }
}

/**
 * Format a date and time (e.g. "08/10/26 20:45").
 */
export function formatDateTime(
  dateValue: string | Date | null | undefined,
  locale: string = 'en-GB',
  style: 'datetime' | 'datetimeLong' = 'datetime'
): string {
  return formatDate(dateValue, locale, style);
}


/**
 * Relative time formatter (e.g., "3 days ago", "in 2 weeks").
 */
export function formatRelativeDate(dateValue: string | Date | null | undefined): string {
  if (!dateValue) return '-';
  try {
    const d = typeof dateValue === 'string' ? parseISO(dateValue) : dateValue;
    if (!isValid(d)) return '-';
    return formatDistanceToNow(d, { addSuffix: true });
  } catch {
    return '-';
  }
}

/**
 * Transforms text to uppercase following Modern Greek typography rules:
 * all-caps Greek words must NOT carry acute accent marks (tonos: Ά, Έ, Ή, Ί, Ό, Ύ, Ώ),
 * while preserving dialytika (diaeresis: Ϊ, Ϋ).
 */
export function toGreekUpper(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .toUpperCase()
    .replace(/[ΆΈΉΊΌΎΏ]/g, (char) => {
      switch (char) {
        case 'Ά': return 'Α';
        case 'Έ': return 'Ε';
        case 'Ή': return 'Η';
        case 'Ί': return 'Ι';
        case 'Ό': return 'Ο';
        case 'Ύ': return 'Υ';
        case 'Ώ': return 'Ω';
        default: return char;
      }
    })
    .replace(/ΐ/g, 'Ϊ')
    .replace(/ΰ/g, 'Ϋ')
    .replace(/ῒ/g, 'Ϊ')
    .replace(/ῢ/g, 'Ϋ');
}

/**
 * Format bytes into human-readable file sizes (e.g. 500 B, 1.5 KB, 24.2 MB, 1.2 GB).
 */
export function formatFileSize(bytes?: number | null): string {
  if (bytes == null || isNaN(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  if (i === 0) return `${bytes} B`;
  const val = bytes / Math.pow(1024, i);
  return `${val.toFixed(val >= 100 ? 0 : 1).replace(/\.0$/, '')} ${units[i]}`;
}


